import { ThemeEvents, VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';

class ProductForm extends HTMLElement {
  #closestSection = null;
  #associatedForm = null;

  get #variantIdInput() {
    return this.#associatedForm?.querySelector('[name="id"]');
  }

  get #submitButton() {
    return this.querySelector('[type="submit"]');
  }

  get #submitButtonText() {
    return this.#submitButton?.querySelector('span');
  }

  get #cartErrorElement() {
    return this.querySelector('[data-ref="cartError"]')
  }

  get #cartErrorText() {
    return this.#cartErrorElement?.querySelector('span');
  }

  get #acceleratedCheckoutButtons() {
    return this.querySelector('[data-ref="acceleratedCheckoutButtons"]');
  }

  get #backInStockButton() {
    return this.querySelector('[data-ref="backInStockButton"]');
  }

  get #quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get #enableBackInStock() {
    return this.getAttribute('enable-back-in-stock') === 'true';
  }

  connectedCallback() {
    this.#closestSection = this.closest('.shopify-section');
    this.#closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const formId = this.getAttribute('form');
    this.#associatedForm = formId ? document.getElementById(formId) : this.closest('form');

    this.#associatedForm?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#closestSection = null;

    this.#associatedForm?.removeEventListener('submit', this.#onSubmit);
    this.#associatedForm = null;
  }

  get #productId() {
    return this.getAttribute('data-product-id');
  }

  /**
   * Handles the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const target = event.target;

    if (target instanceof HTMLElement && target.getAttribute('data-product-id') !== this.#productId) {
      return;
    }

    const { variant, data: { html } } = event.detail;

    this.#hideErrorMessage();

    this.#updateVariantIdInput(variant);
    this.#updateBuyButtons(variant);
    this.#updateQuantitySelector(variant);
  };

  /**
   * Handles the submit event for the product form.
   * @param {Event} event - The submit event.
   */
  #onSubmit = async (event) => {
    event.preventDefault();

    if (this.#submitButton?.disabled) return;

    const form = this.#associatedForm;
    if (!form) throw new Error('No associated product form found');

    const formData = new FormData(form);

    formData.set('sections', 'cart-notification-product');

    this.#setLoading(true);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        this.#showErrorMessage(data.message);

        this.dispatchEvent(new CartErrorEvent(
          this.id,
          data.message,
          data.description,
          data.errors,
        ));
      } else {
        this.#hideErrorMessage();

        this.dispatchEvent(new CartAddEvent(data, this.id, {
          source: 'product-form',
          productId: this.#productId,
          variantId: formData.get('id')?.toString(),
          itemCount: Number(formData.get('quantity')) || 1,
          sections: data.sections,
        }));
      }
    } catch (error) {
      console.error('Add to cart failed', error);
    } finally {
      this.#setLoading(false);
    }
  };

  #showErrorMessage(message) {
    const element = this.#cartErrorElement;
    const elementText = this.#cartErrorText;
    if (!element || !elementText) return;

    elementText.textContent = message;
    element.removeAttribute('hidden');
  }

  #hideErrorMessage() {
    const element = this.#cartErrorElement;
    const elementText = this.#cartErrorText;
    if (!element || !elementText) return;

    elementText.textContent = '';
    element.setAttribute('hidden', '');
  }

  #setLoading(loading) {
    this.#submitButton?.toggleAttribute('aria-busy', loading);
    this.toggleAttribute('data-loading', loading);
  }

  #updateVariantIdInput(variant) {
    const input = this.#variantIdInput;
    if (!input) return;

    input.value = variant?.id ?? '';
  }

  #updateBuyButtons(variant) {
    const button = this.#submitButton;
    const buttonText = this.#submitButtonText;

    const backInStockButton = this.#backInStockButton;

    if (!variant) {
      button.disabled = true;
      button.classList.remove('hidden');
      buttonText.textContent = Theme.strings.product.unavailable;

      backInStockButton?.classList.add('hidden');

      this.#acceleratedCheckoutButtons?.setAttribute('hidden', '');

      return;
    }

    if (!variant.available) {
      this.#acceleratedCheckoutButtons?.setAttribute('hidden', '');

      if (this.#enableBackInStock && backInStockButton) {
        button.classList.add('hidden');
        backInStockButton.classList.remove('hidden');
      } else {
        button.disabled = true;
        buttonText.textContent = Theme.strings.product.sold_out;
      }
    } else {
      button.disabled = false;
      button.classList.remove('hidden');
      buttonText.textContent = Theme.strings.product.add_to_cart;

      backInStockButton?.classList.add('hidden');

      this.#acceleratedCheckoutButtons?.removeAttribute('hidden');
    }
  }

  #updateQuantitySelector(variant) {
    const quantitySelector = this.#quantitySelector;
    if (!quantitySelector) return;

    if (!variant) {
      quantitySelector.setAvailability(false);
      return;
    }

    quantitySelector.setAvailability(variant.available);
    quantitySelector.updateConstraints(
      String(variant.quantity_rule?.min ?? 1),
      String(variant.quantity_rule?.max ?? parseInt(quantitySelector.dataset.defaultMaxQuantity)),
      String(variant.quantity_rule?.increment ?? 1)
    );
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
